import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageTransition from '../../components/PageTransition';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft, FiPlus, FiEdit3, FiTrash2, FiMenu, FiCheck } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';
import AddQuestionModal from '../../components/screener/AddQuestionModal';
import RecruitmentTargetSection from '../../components/screener/RecruitmentTargetSection';

const ScreenerBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: pData } = await supabase.from(TABLES.PROJECTS).select('name, target_n').eq('id', projectId).single();
      setProject(pData);

      const { data: qData, error: qError } = await supabase
        .from(TABLES.SCREENER_QUESTIONS)
        .select('*')
        .eq('project_id', projectId)
        .order('order_index', { ascending: true });
      
      if (qError) throw qError;
      setQuestions(qData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      setActionLoading(true);
      if (editingQuestion) {
        const { error } = await supabase
          .from(TABLES.SCREENER_QUESTIONS)
          .update(formData)
          .eq('id', editingQuestion.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from(TABLES.SCREENER_QUESTIONS).insert([{
          ...formData,
          project_id: projectId,
          order_index: questions.length
        }]);
        if (error) throw error;
      }
      setIsModalOpen(false);
      setEditingQuestion(null);
      fetchData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const deleteQuestion = async (id) => {
    if (!confirm('Are you sure you want to delete this question?')) return;
    const { error } = await supabase.from(TABLES.SCREENER_QUESTIONS).delete().eq('id', id);
    if (!error) fetchData();
  };

  if (loading) return (
    <div className="h-full flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rutabaga-pink"></div>
    </div>
  );

  return (
    <PageTransition className="max-w-4xl mx-auto p-12">
      <div className="flex items-center justify-between mb-12">
        <div>
          <button 
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-rutabaga-dark transition-colors mb-4 font-bold"
          >
            <SafeIcon icon={FiArrowLeft} /> Back to Projects
          </button>
          <h1 className="text-4xl font-black text-rutabaga-dark tracking-tight">
            Screener Builder
          </h1>
          <p className="text-gray-500 font-medium mt-1">
            Project: <span className="text-rutabaga-pink font-bold">{project?.name}</span>
          </p>
        </div>
        
        <button 
          onClick={() => navigate('/projects')}
          className="rutabaga-button-primary flex items-center gap-2"
        >
          <SafeIcon icon={FiCheck} strokeWidth={3} />
          <span>Done Building</span>
        </button>
      </div>

      <div className="space-y-4 mb-8">
        {questions.map((q, idx) => (
          <div key={q.id} className="bg-white border border-gray-200 rounded-md p-6 flex gap-6 group hover:border-rutabaga-pink/20 transition-all">
            <div className="flex flex-col items-center gap-2 pt-1">
              <div className="text-gray-300 group-hover:text-gray-400 cursor-grab">
                <SafeIcon icon={FiMenu} />
              </div>
              <span className="text-[10px] font-black text-gray-300">{idx + 1}</span>
            </div>
            
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${
                  q.question_type === 'single_select' ? 'bg-indigo-50 text-indigo-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {q.question_type === 'single_select' ? 'Segmentation' : 'Additional'}
                </span>
                {q.tag_key && (
                  <span className="text-[10px] font-bold text-gray-400">Tag: {q.tag_key}</span>
                )}
              </div>
              <h3 className="font-bold text-rutabaga-dark text-lg mb-4">{q.question_text}</h3>
              
              {q.options?.length > 0 && (
                <>
                  <div className="flex flex-wrap gap-2">
                    {q.options.map((opt, i) => (
                      <span key={i} className="text-[10px] font-bold bg-gray-50 text-gray-500 px-2 py-1 rounded border border-gray-100">
                        {opt.label}
                      </span>
                    ))}
                  </div>

                  {q.question_type === 'single_select' && (
                    <RecruitmentTargetSection 
                      question={q} 
                      targetN={project?.target_n} 
                      projectId={projectId} 
                    />
                  )}
                </>
              )}
            </div>

            <div className="flex gap-2 shrink-0">
              <button 
                onClick={() => { setEditingQuestion(q); setIsModalOpen(true); }}
                className="p-2 text-gray-400 hover:text-rutabaga-dark hover:bg-gray-50 rounded-md transition-all"
              >
                <SafeIcon icon={FiEdit3} />
              </button>
              <button 
                onClick={() => deleteQuestion(q.id)}
                className="p-2 text-gray-400 hover:text-rutabaga-pink hover:bg-red-50 rounded-md transition-all"
              >
                <SafeIcon icon={FiTrash2} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <button 
        onClick={() => { setEditingQuestion(null); setIsModalOpen(true); }}
        className="w-full py-8 rounded-md border-2 border-dashed border-gray-300 text-gray-500 hover:border-rutabaga-pink hover:text-rutabaga-pink transition-all flex flex-col items-center justify-center gap-2 group"
      >
        <SafeIcon icon={FiPlus} className="text-2xl group-hover:scale-110 transition-transform" />
        <span className="text-xs font-black uppercase tracking-widest">Add Screener Question</span>
      </button>

      <AddQuestionModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
        loading={actionLoading}
        initialData={editingQuestion}
      />
    </PageTransition>
  );
};

export default ScreenerBuilder;